// src/pages/ProviderTaxRequests.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

import api, { authApi } from "../api/axios";
import { useAuth } from "../context/AuthContext";

const PROVIDER_API_PATH = "/provider";
const DRIVER_API = "/api/v1/driver-details";
const PROVIDER_ID = () => JSON.parse(localStorage.getItem("provider") || "{}")?._id;
const DEFAULT_WA_MESSAGE = "आपका Tax Government Side से Pending है, इसे Complete होने में कुछ Extra Time लग सकता है।";

const normalizePhone = (num) => {
    if (!num) return null;
    let digits = String(num).replace(/\D/g, "");
    if (digits.length === 10) digits = "91" + digits;
    return digits;
};

const openWhatsapp = async (driverId, fallbackContact, defaultMsg = DEFAULT_WA_MESSAGE) => {
    console.log(driverId)
    let contact = fallbackContact;

    if (driverId) {
        try {
            const res = await authApi.get(`${DRIVER_API}/${driverId}`);
            contact = res.data?.data?.driver_contact_number || fallbackContact;
        } catch (err) {
            // fetch fail ho gaya to fallback contact hi use hoga
        }
    }

    const phone = normalizePhone(contact);
    if (!phone) {
        Swal.fire({ icon: "warning", title: "Driver number not found" });
        return;
    }

    const { value: message, isConfirmed } = await Swal.fire({
        title: "Send WhatsApp Message",
        input: "textarea",
        inputValue: defaultMsg,
        inputAttributes: { rows: 4 },
        showCancelButton: true,
        confirmButtonText: "Open WhatsApp",
    });
    if (!isConfirmed) return;

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message || "")}`;
    window.open(url, "_blank");
};

const WhatsappIcon = ({ className = "h-4 w-4" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
        <path d="M12.001 2.003c-5.514 0-9.997 4.483-9.997 9.997 0 1.762.464 3.484 1.346 4.997L2.06 22l5.13-1.264a9.958 9.958 0 004.811 1.225h.001c5.514 0 9.997-4.483 9.997-9.997 0-2.67-1.039-5.18-2.926-7.067A9.933 9.933 0 0012.001 2.003zm0 18.045c-1.55 0-3.05-.416-4.36-1.203l-.313-.187-3.045.75.813-2.968-.204-.305a8.028 8.028 0 01-1.24-4.278c0-4.454 3.626-8.08 8.08-8.08a8.03 8.03 0 015.71 2.365 8.032 8.032 0 012.37 5.716c0 4.454-3.627 8.08-8.08 8.08z" />
    </svg>
);

// ---------- Tax type tabs ----------
// "all" is a UI-only pseudo tab: no tax_type param is sent to the API when selected.
const taxTypes = [
    { key: "all", label: "All" },
    { key: "road_tax", label: "Road Tax Pay" },
    { key: "three_month_tax", label: "3 Month Wala Border Tax" },
    { key: "aitp", label: "AITP Authorisation" },
];

const ROAD_TAX_DURATIONS = [
    { key: "1_month", label: "1 Month" },
    { key: "Quarterly", label: "Quarterly" },
    { key: "Yearly", label: "Yearly" },
    { key: "Lifetime", label: "Lifetime" },
];

const STATUS_OPTIONS = [
    "tax-checking",
    "pending-at-payment-of-checking-fee",
    "refunded",
    "cancelled",
    "completed",
    "final_payment_pending",
    "final_payment_done",
];

const STATUS_STYLES = {
    "tax-checking": "bg-amber-50 text-amber-700 ring-amber-600/20",
    "pending-at-payment-of-checking-fee": "bg-orange-50 text-orange-700 ring-orange-600/20",
    refunded: "bg-sky-50 text-sky-700 ring-sky-600/20",
    cancelled: "bg-rose-50 text-rose-700 ring-rose-600/20",
    completed: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    final_payment_pending: "bg-violet-50 text-violet-700 ring-violet-600/20",
    final_payment_done: "bg-teal-50 text-teal-700 ring-teal-600/20",
};

const StatusBadge = ({ status }) => (
    <span
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${STATUS_STYLES[status] || "bg-gray-50 text-gray-600 ring-gray-500/20"
            }`}
    >
        {status?.replace(/-|_/g, " ")}
    </span>
);

const Spinner = ({ className = "h-5 w-5" }) => (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
);

const Field = ({ label, children }) => (
    <div>
        <div className="text-xs font-medium text-gray-500 mb-0.5">{label}</div>
        <div className="text-sm font-medium text-gray-900">{children}</div>
    </div>
);

// ---------- live timer ----------
const useNow = (tickMs = 1000) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), tickMs);
        return () => clearInterval(id);
    }, [tickMs]);
    return now;
};

const formatDur = (ms) => {
    ms = Math.max(0, Math.abs(ms));
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${h}h ${m}m ${s}s`;
};

// Road Tax / Three Month Tax / AITP: payment window is no longer a fixed
// 1-hour countdown. Instead the driver gets till 11:59:59 PM (IST) of the
// SAME calendar day the deadline field was set on. Past midnight the
// request is expected to auto-cancel/timeout on the backend.
const getSameDayEndOfDayIST = (dateInput) => {
    const d = dateInput ? new Date(dateInput) : new Date();
    const istDateStr = d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    return new Date(`${istDateStr}T23:59:59.999+05:30`);
};

const TaxTimer = ({ deadline, status, size = "sm" }) => {
    const now = useNow();
    if (status === "completed" || status === "cancelled" || status === "refunded") return null;
    if (!deadline) return <span className="text-xs text-gray-400">-</span>;

    const endOfDay = getSameDayEndOfDayIST(deadline);
    const diff = endOfDay.getTime() - now;
    const cls = size === "lg" ? "text-sm font-semibold" : "text-xs font-medium";

    if (diff > 0) {
        return (
            <span className={`${cls} text-emerald-600 whitespace-nowrap`}>
                {formatDur(diff)} left (till 11:59 PM)
            </span>
        );
    }
    return (
        <span className={`${cls} text-rose-600 whitespace-nowrap`}>
            Time over — awaiting auto-cancel
        </span>
    );
};
const EXTEND_MINUTES_OPTIONS = [20, 30, 40, 50];
const ProviderTaxRequests = () => {
    const { employee } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // Default tax_type is "all" — tabs replace the old dropdown.
    const taxType = searchParams.get("tax_type") || "all";
    const status = searchParams.get("status") || "";
    const search = searchParams.get("search") || "";
    const page = parseInt(searchParams.get("page") || "1", 10);
    const limit = parseInt(searchParams.get("limit") || "20", 10);

    const [requests, setRequests] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
    const [loading, setLoading] = useState(false);
    const [extendingTimer, setExtendingTimer] = useState(false);
    // --- transfer ---
    const [providers, setProviders] = useState([]);
    const [providersLoading, setProvidersLoading] = useState(false);
    const [providerSearch, setProviderSearch] = useState("");
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [selectedTax, setSelectedTax] = useState(null);
    const [reason, setReason] = useState("");
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [issueFiles, setIssueFiles] = useState([]);
    const issueImagesInputRef = useRef(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [searchInput, setSearchInput] = useState(search);

    // --- view detail ---
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [order, setOrder] = useState(null);
    const [orderLoading, setOrderLoading] = useState(false);

    // driver info
    const [driverModalOpen, setDriverModalOpen] = useState(false);
    const [driverData, setDriverData] = useState(null);
    const [driverLoading, setDriverLoading] = useState(false);
    const [driverError, setDriverError] = useState(null);

    // final tax
    const [feeForm, setFeeForm] = useState({ final_tax_amount: "", service_fee: "", remark: "", duration: "" });
    const [savingFee, setSavingFee] = useState(false);

    // pdf / refund
    const pdfInputRef = useRef(null);
    const refundInputRef = useRef(null);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [refunding, setRefunding] = useState(false);

    const updateParams = (updates) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([k, v]) => {
            if (v === "" || v === null || v === undefined) next.delete(k);
            else next.set(k, v);
        });
        setSearchParams(next);
    };

    // ---------- list ----------
    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/border-tax/three/provider/requests`, {
                params: {
                    // "all" tab → don't send tax_type at all, backend returns every type
                    tax_type: taxType === "all" ? undefined : taxType,
                    tax_status: status,
                    search,
                    page,
                    limit,
                },
            });
            setRequests(data?.data || []);
            setPagination(data?.pagination || { total: 0, totalPages: 1 });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Unable to fetch tax requests.",
            });
        } finally {
            setLoading(false);
        }
    }, [taxType, status, search, page, limit]);

    const fetchProviders = async () => {
        setProvidersLoading(true);
        try {
            const { data } = await api.get(PROVIDER_API_PATH);
            setProviders(data?.data || []);
        } catch (error) {
            Swal.fire({ icon: "error", title: "Failed", text: error.response?.data?.message || "Unable to load providers." });
        } finally {
            setProvidersLoading(false);
        }
    };

    // ---------- transfer ----------
    const transferToProvider = async () => {
        if (!selectedTax) return Swal.fire("Warning", "Please select a tax request.", "warning");
        if (!selectedProvider) return Swal.fire("Warning", "Please select a provider.", "warning");
        if (!reason.trim()) return Swal.fire("Warning", "Reason is required.", "warning");

        try {
            setLoading(true);
            await api.post(`/admin/three-month-tax/transfer`, {
                tax_id: selectedTax._id,
                new_provider: selectedProvider._id,
                reason,
            });
            Swal.fire("Success", "Request transferred successfully.", "success");
            setReason("");
            setSelectedProvider(null);
            setSelectedTax(null);
            setShowTransferModal(false);
            fetchRequests();
        } catch (error) {
            Swal.fire({ icon: "error", title: "Transfer Failed", text: error.response?.data?.message || "Something went wrong." });
        } finally {
            setLoading(false);
        }
    };

    const openTransferModal = (req) => {
        setSelectedTax(req);
        setSelectedProvider(null);
        setReason("");
        setShowTransferModal(true);
    };

    const handleExtendTimer = async () => {
        const { value: extra_minutes, isConfirmed: step1 } = await Swal.fire({
            title: "Extend Timer",
            input: "select",
            inputLabel: "Extra time",
            inputPlaceholder: "Select extra minutes",
            inputOptions: EXTEND_MINUTES_OPTIONS.reduce((acc, m) => {
                acc[m] = `${m} Minutes`;
                return acc;
            }, {}),
            inputValidator: (v) => (!v ? "Select extra time" : undefined),
            showCancelButton: true,
            confirmButtonText: "Next",
        });
        if (!step1 || !extra_minutes) return;

        const { value: remark, isConfirmed: step2 } = await Swal.fire({
            title: `Extend by ${extra_minutes} minutes?`,
            input: "textarea",
            inputPlaceholder: "Reason (optional) — e.g. Government server down",
            showCancelButton: true,
            confirmButtonText: "Confirm & Notify Driver",
        });
        if (!step2) return;

        setExtendingTimer(true);
        try {
            await api.post(`/admin/border-tax/three/provider/border-tax/three/extend-timer`, {
                tax_id: order._id,
                extra_minutes: Number(extra_minutes),
                remark: remark || "",
            });
            Swal.fire({
                icon: "success",
                title: `Timer extended by ${extra_minutes} minutes`,
                text: "Driver notified.",
                timer: 1500,
                showConfirmButton: false,
            });
            fetchOrder(order._id);
            fetchRequests();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed", text: err.response?.data?.message || "Something went wrong" });
        } finally {
            setExtendingTimer(false);
        }
    };

    // ---------- view detail ----------
    const fetchOrder = async (id) => {
        setOrderLoading(true);
        try {
            const { data } = await api.get(`/admin/border-tax/three/admin/request/${id}`);
            setOrder(data?.data || null);
            setFeeForm({
                final_tax_amount: data?.data?.final_tax_amount || "",
                service_fee: data?.data?.service_fee || "",
                remark: "",
                duration: data?.data?.duration || ""
            });
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Unable to fetch request." });
        } finally {
            setOrderLoading(false);
        }
    };

    const openDetailModal = (req) => {
        setShowDetailModal(true);
        setOrder(req);
        setDriverData(null);
        setDriverError(null);
        fetchOrder(req._id);
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setOrder(null);
        setDriverData(null);
    };

    // ---------- driver info ----------
    const showDriverInfo = async () => {
        if (!order?.driver_id) {
            Swal.fire({ icon: "info", title: "No driver ID" });
            return;
        }
        setDriverModalOpen(true);
        setDriverLoading(true);
        setDriverError(null);
        setDriverData(null);
        try {
            const res = await authApi.get(`${DRIVER_API}/${order.driver_id}`);
            setDriverData(res.data?.data || null);
        } catch (err) {
            setDriverError(err.response?.data?.message || "Failed to fetch driver");
        } finally {
            setDriverLoading(false);
        }
    };

    // ---------- status change ----------
    const handleStatusChange = async () => {
        const { value: form } = await Swal.fire({
            title: "Change status",
            html: `
        <select id="swal-status" class="swal2-select" style="width:80%;margin:0 auto 10px;display:block">
          ${STATUS_OPTIONS.map((s) => `<option value="${s}" ${s === order.tax_status ? "selected" : ""}>${s}</option>`).join("")}
        </select>
        <textarea id="swal-remark" class="swal2-textarea" placeholder="Remark (optional)" style="width:80%"></textarea>
      `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Update",
            preConfirm: () => ({
                tax_status: document.getElementById("swal-status").value,
                remark: document.getElementById("swal-remark").value,
            }),
        });
        if (!form) return;
        try {
            Swal.fire({ title: "Updating...", didOpen: () => Swal.showLoading() });
            await api.put(`/admin/border-tax/three/admin/request/${order._id}/status`, form);
            Swal.close();
            Swal.fire({ icon: "success", title: "Status updated", timer: 1200, showConfirmButton: false });
            fetchOrder(order._id);
            fetchRequests();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Update failed", text: err.response?.data?.message || "Something went wrong" });
        }
    };

    // ---------- final tax (always editable, resubmit-safe) ----------
    const submitFinalTax = async () => {
        const hasAmount = feeForm.final_tax_amount !== "" && feeForm.final_tax_amount !== null;
        if (!hasAmount && issueFiles.length === 0) {
            Swal.fire({ icon: "warning", title: "Enter final tax amount or attach an issue screenshot" });
            return;
        }

        const isUpdate = !!order?.final_tax_amount;
        if (hasAmount && isUpdate) {
            const confirm = await Swal.fire({
                icon: "question",
                title: "Update final tax?",
                text: `Existing ₹${order.final_tax_amount} will be overwritten with ₹${feeForm.final_tax_amount}.`,
                showCancelButton: true,
                confirmButtonText: "Yes, update",
            });
            if (!confirm.isConfirmed) return;
        }

        setSavingFee(true);
        try {
            let driverName = "User";
            let driverContact = "";
            try {
                const res = await authApi.get(`${DRIVER_API}/${order.driver_id}`);
                driverName = res.data?.data?.driver_name || driverName;
                driverContact = res.data?.data?.phone || "";
            } catch (_) {
                // ignore, fallback used
            }

            const fd = new FormData();
            fd.append("tax_id", order._id);
            fd.append("provider_id", order?.assigned_partner?._id || PROVIDER_ID());
            if (hasAmount) fd.append("final_tax_amount", Number(feeForm.final_tax_amount));
            fd.append("service_fee", Number(feeForm.service_fee) || 0);
            fd.append("driver_name", driverName);
            fd.append("driver_contact_number", driverContact);
            fd.append("remark", feeForm.remark || "");
            if (order.tax_type === "road_tax" && feeForm.duration) {
                fd.append("duration", feeForm.duration);
            }
            issueFiles.forEach((file) => fd.append("issue_images", file));

            await api.post(`/admin/border-tax/three/provider/final-tax`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            Swal.fire({
                icon: "success",
                title: hasAmount ? (isUpdate ? "Final tax updated" : "Final tax saved") : "Issue screenshot sent",
                timer: 1200,
                showConfirmButton: false,
            });
            setIssueFiles([]);
            fetchOrder(order._id);
            fetchRequests();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed to save", text: err.response?.data?.message || "Something went wrong" });
        } finally {
            setSavingFee(false);
        }
    };

    const handleIssueFilesPick = () => issueImagesInputRef.current?.click();

    const onIssueFilesChange = (e) => {
        const picked = Array.from(e.target.files || []).slice(0, 5);
        setIssueFiles(picked);
    };
    // ---------- pdf upload ----------
    const handlePdfPick = () => pdfInputRef.current?.click();

    const submitPdfUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingPdf(true);
        try {
            const fd = new FormData();
            fd.append("tax_id", order._id);
            fd.append("provider_id", order?.assigned_partner?._id || PROVIDER_ID());
            fd.append("border_tax_pdf", file);
            await api.post(`/admin/border-tax/three/provider/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            Swal.fire({ icon: "success", title: "PDF uploaded, status → completed", timer: 1500, showConfirmButton: false });
            fetchOrder(order._id);
            fetchRequests();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Upload failed", text: err.response?.data?.message || "Something went wrong" });
        } finally {
            setUploadingPdf(false);
            e.target.value = "";
        }
    };

    // ---------- refund ----------
    const handleRefundPick = () => refundInputRef.current?.click();

    const submitRefund = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const { value: remark } = await Swal.fire({
            title: "Refund remark",
            input: "textarea",
            inputPlaceholder: "Reason for refund...",
            showCancelButton: true,
            confirmButtonText: "Confirm refund",
        });
        if (remark === undefined) {
            e.target.value = "";
            return;
        }
        setRefunding(true);
        try {
            const fd = new FormData();
            fd.append("tax_id", order._id);
            fd.append("provider_id", order?.assigned_partner?._id || PROVIDER_ID());
            fd.append("remark", remark || "");
            fd.append("refund_screen_shot", file);
            await api.post(`/admin/border-tax/three/provider/refund`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            Swal.fire({ icon: "success", title: "Refund recorded", timer: 1500, showConfirmButton: false });
            fetchOrder(order._id);
            fetchRequests();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Refund failed", text: err.response?.data?.message || "Something went wrong" });
        } finally {
            setRefunding(false);
            e.target.value = "";
        }
    };

    useEffect(() => { fetchRequests(); }, [fetchRequests]);
    useEffect(() => { fetchProviders(); }, []);
    useEffect(() => {
        const t = setTimeout(() => updateParams({ search: searchInput, page: 1 }), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const filteredProviders = providers.filter((p) =>
        `${p.name || ""} ${p.callingNumber || p.whatsapp_number || ""}`.toLowerCase().includes(providerSearch.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50/50 p-3 sm:p-6">
            <div className="mx-auto max-w-7xl">
                {/* ---------- HEADER ---------- */}
                <div className="mb-5 flex flex-col gap-1">
                    <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                        New Tax Requests (AITP Authorisation, Road Tax & Three Month Tax)
                    </h1>
                    <p className="text-sm text-gray-500">Track, transfer, and settle border tax requests by provider.</p>
                </div>

                {/* ---------- TAX TYPE TABS ---------- */}
                <div className="mb-4 overflow-x-auto">
                    <div className="inline-flex min-w-full gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm sm:min-w-0">
                        {taxTypes.map((t) => {
                            const active = taxType === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => updateParams({ tax_type: t.key === "all" ? "" : t.key, page: 1 })}
                                    className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition sm:flex-none sm:px-4 ${active
                                            ? "bg-blue-600 text-white shadow-sm"
                                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                        }`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ---------- FILTER BAR ---------- */}
                <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <select
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            value={status}
                            onChange={(e) => updateParams({ status: e.target.value, page: 1 })}
                        >
                            <option value="">All Statuses</option>
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/-/g, " ")}</option>)}
                        </select>

                        <div className="relative flex-1 min-w-[200px]">
                            <svg className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                            </svg>
                            <input
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                placeholder="Search vehicle / chassis number"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>

                        <select
                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            value={limit}
                            onChange={(e) => updateParams({ limit: e.target.value, page: 1 })}
                        >
                            {[10, 20, 50, 100].map((l) => <option key={l} value={l}>{l} / page</option>)}
                        </select>
                    </div>
                </div>

                {/* ---------- TABLE (desktop) ---------- */}
                <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:block">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    {["Vehicle", "Chassis", "State", "Seat Type", "Status", "Assigned Partner", "Transferred?", "Timer", ""].map((h) => (
                                        <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr><td colSpan={9} className="p-8 text-center text-gray-400"><Spinner className="mx-auto h-6 w-6" /></td></tr>
                                ) : requests.length === 0 ? (
                                    <tr><td colSpan={9} className="p-8 text-center text-gray-400">No requests found.</td></tr>
                                ) : (
                                    requests.map((r) => (
                                        <tr key={r._id} className="transition hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{r.vehicle_number}</td>
                                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.chassis_number}</td>
                                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.main_state?.main_state || "-"}</td>
                                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.seat_type}</td>
                                            <td className="px-4 py-3"><StatusBadge status={r.tax_status} /></td>
                                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.assigned_partner?.name || "-"}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {r.wasReceivedViaTransfer ? (
                                                    <span className="text-xs font-medium text-emerald-600">Yes</span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">No</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <TaxTimer deadline={r.payment_done_check_time} status={r.tax_status} />
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex gap-3">
                                                    <button className="text-sm font-medium text-emerald-600 hover:text-emerald-700" onClick={() => openDetailModal(r)}>View</button>
                                                    <button className="text-sm font-medium text-blue-600 hover:text-blue-700" onClick={() => openTransferModal(r)}>Transfer</button>
                                                    <button
                                                        className="text-green-600 hover:text-green-700"
                                                        title="WhatsApp Driver"
                                                        onClick={async () => await openWhatsapp(r?.driver_id, r?.contact)}
                                                    >
                                                        <WhatsappIcon />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ---------- CARDS (mobile) ---------- */}
                <div className="space-y-3 md:hidden">
                    {loading ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-400 shadow-sm"><Spinner className="mx-auto h-6 w-6" /></div>
                    ) : requests.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-400 shadow-sm">No requests found.</div>
                    ) : (
                        requests.map((r) => (
                            <div key={r._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <div className="mb-2 flex items-start justify-between gap-2">
                                    <div>
                                        <div className="font-semibold text-gray-900">{r.vehicle_number}</div>
                                        <div className="text-xs text-gray-500">{r.chassis_number}</div>
                                    </div>
                                    <StatusBadge status={r.tax_status} />
                                </div>
                                <div className="mb-2">
                                    <TaxTimer deadline={r.payment_done_check_time} status={r.tax_status} size="lg" />
                                </div>
                                <div className="mb-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                    <div><span className="text-gray-400">State:</span> {r.main_state?.main_state || "-"}</div>
                                    <div><span className="text-gray-400">Seat Type:</span> {r.seat_type}</div>
                                    <div><span className="text-gray-400">Partner:</span> {r.assigned_partner?.name || "-"}</div>
                                    <div><span className="text-gray-400">Transferred:</span> {r.wasReceivedViaTransfer ? "Yes" : "No"}</div>
                                </div>
                                <div className="flex gap-4 border-t border-gray-100 pt-2">
                                    <button className="text-sm font-medium text-emerald-600" onClick={() => openDetailModal(r)}>View</button>
                                    <button className="text-sm font-medium text-blue-600" onClick={() => openTransferModal(r)}>Transfer</button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* ---------- PAGINATION ---------- */}
                <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
                    <span className="text-sm text-gray-500">Total: {pagination.total}</span>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={page <= 1}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            onClick={() => updateParams({ page: page - 1 })}
                        >
                            Prev
                        </button>
                        <span className="text-sm text-gray-600">Page {page} / {pagination.totalPages || 1}</span>
                        <button
                            disabled={page >= (pagination.totalPages || 1)}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            onClick={() => updateParams({ page: page + 1 })}
                        >
                            Next
                        </button>
                    </div>
                </div>

                {/* ---------- TRANSFER MODAL ---------- */}
                {showTransferModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
                        <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-base font-semibold text-gray-900">Transfer {selectedTax?.vehicle_number}</h3>
                                <button className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" onClick={() => setShowTransferModal(false)}>✕</button>
                            </div>

                            <input
                                className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                placeholder="Search provider"
                                value={providerSearch}
                                onChange={(e) => setProviderSearch(e.target.value)}
                            />
                            <div className="mb-3 max-h-40 overflow-y-auto rounded-lg border border-gray-200">
                                {providersLoading ? (
                                    <div className="flex items-center gap-2 p-3 text-sm text-gray-400"><Spinner className="h-4 w-4" /> Loading providers...</div>
                                ) : filteredProviders.length === 0 ? (
                                    <div className="p-3 text-sm text-gray-400">No providers found.</div>
                                ) : (
                                    filteredProviders.map((p) => (
                                        <div
                                            key={p._id}
                                            className={`cursor-pointer border-b border-gray-100 p-2.5 text-sm last:border-0 hover:bg-gray-50 ${selectedProvider?._id === p._id ? "bg-blue-50 font-medium text-blue-700" : "text-gray-700"
                                                }`}
                                            onClick={() => setSelectedProvider(p)}
                                        >
                                            {p.name} — {p.callingNumber || p.whatsapp_number}
                                        </div>
                                    ))
                                )}
                            </div>
                            <textarea
                                className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                rows={3}
                                placeholder="Reason for transfer"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                            <div className="flex justify-end gap-2">
                                <button className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={() => setShowTransferModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                                    disabled={loading}
                                    onClick={transferToProvider}
                                >
                                    {loading && <Spinner className="h-4 w-4" />}
                                    Confirm Transfer
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------- DETAIL MODAL ---------- */}
                {showDetailModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
                        <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-xl">
                            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white/95 px-5 py-4 backdrop-blur">
                                <h3 className="text-base font-semibold text-gray-900">{order?.vehicle_number} — Details</h3>
                                <button className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" onClick={closeDetailModal}>✕</button>
                            </div>

                            <div className="p-5">
                                {orderLoading || !order ? (
                                    <div className="flex justify-center p-8"><Spinner className="h-6 w-6 text-gray-400" /></div>
                                ) : (
                                    <>
                                        <div className="mb-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl bg-gray-50 p-4 sm:grid-cols-3">
                                            <Field label="Chassis">{order.chassis_number}</Field>
                                            <Field label="State">{order.main_state?.main_state || "-"}</Field>
                                            <Field label="Seat Type">{order.seat_type}</Field>
                                            <Field label="Tax Type">{order.tax_type}</Field>
                                            {order.duration && (
                                                <Field label="Tax Duration">{order.duration}</Field>
                                            )}

                                            <Field label="Status"><StatusBadge status={order.tax_status} /></Field>
                                            <Field label="Assigned Partner">{order.assigned_partner?.name || "-"}</Field>
                                            <Field label="Final Tax Amount">₹{order.final_tax_amount || 0}</Field>
                                            <Field label="Service Fee">₹{order.service_fee || 0}</Field>
                                            <Field label="Total Payable">₹{order.total_pay_able_fee || 0}</Field>
                                            <Field label="Transferred?">{order.wasReceivedViaTransfer ? "Yes" : "No"}</Field>
                                            <Field label="Payment Timer"><TaxTimer status={order.tax_status} deadline={order.payment_done_check_time} size="lg" /></Field>
                                        </div>

                                        <div className="mb-4 flex flex-wrap gap-2">
                                            <button className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={showDriverInfo}>
                                                Driver Details
                                            </button>
                                            <button className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={handleStatusChange}>
                                                Change Status
                                            </button>
                                            <button
                                                className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={handlePdfPick}
                                                disabled={uploadingPdf}
                                            >
                                                {uploadingPdf && <Spinner className="h-4 w-4" />}
                                                {uploadingPdf ? "Uploading..." : "Upload PDF"}
                                            </button>
                                            <input type="file" accept="application/pdf" ref={pdfInputRef} className="hidden" onChange={submitPdfUpload} />

                                            <button
                                                className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={handleRefundPick}
                                                disabled={refunding}
                                            >
                                                {refunding && <Spinner className="h-4 w-4" />}
                                                {refunding ? "Processing..." : "Upload Refund"}
                                            </button>
                                            <input type="file" accept="image/*" ref={refundInputRef} className="hidden" onChange={submitRefund} />
                                        </div>
                                        {(order.tax_status === "tax-checking" || order.tax_status === "final_payment_pending") && (
                                            <button
                                                className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                                                onClick={handleExtendTimer}
                                                disabled={extendingTimer}
                                            >
                                                {extendingTimer && <Spinner className="h-4 w-4" />}
                                                {extendingTimer ? "Extending..." : "Extend Timer"}
                                            </button>
                                        )}

                                        <div className="mb-4 flex flex-wrap gap-4">
                                            {order.border_tax_pdf?.url && (
                                                <a href={order.border_tax_pdf.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 underline-offset-2 hover:underline">
                                                    View Uploaded Border Tax PDF
                                                </a>
                                            )}
                                            {order.payment_screen_shot?.url && (
                                                <div className="mt-4">
                                                    <div className="mb-2 text-xs font-medium text-gray-500">
                                                        Payment Screenshot
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setPreviewImage(order.payment_screen_shot.url)
                                                        }
                                                        className="group relative h-24 w-24 overflow-hidden rounded-lg border border-gray-200"
                                                    >
                                                        <img
                                                            src={order.payment_screen_shot.url}
                                                            alt="Payment Screenshot"
                                                            className="h-full w-full object-cover transition group-hover:scale-105"
                                                        />

                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 text-white transition group-hover:bg-black/40">
                                                            <span className="text-xs opacity-0 group-hover:opacity-100">
                                                                View
                                                            </span>
                                                        </div>
                                                    </button>
                                                </div>
                                            )}
                                            {order.refund_payment_screen_shot?.url && (
                                                <a href={order.refund_payment_screen_shot.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 underline-offset-2 hover:underline">
                                                    View Refund Screenshot
                                                </a>
                                            )}
                                        </div>

                                        <div className="mb-5 rounded-xl border border-gray-200 p-4">
                                            <div className="mb-3 flex items-center justify-between">
                                                <h4 className="text-sm font-semibold text-gray-900">
                                                    {order.final_tax_amount ? "Update Final Tax" : "Submit Final Tax"}
                                                </h4>
                                                {order.final_tax_amount ? (
                                                    <span className="text-xs text-gray-400">Current: ₹{order.final_tax_amount}</span>
                                                ) : null}
                                            </div>
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                <input
                                                    type="number"
                                                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                    placeholder="Final tax amount"
                                                    value={feeForm.final_tax_amount}
                                                    onChange={(e) => setFeeForm((f) => ({ ...f, final_tax_amount: e.target.value }))}
                                                />
                                                <input
                                                    type="number"
                                                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                    placeholder="Service fee"
                                                    value={feeForm.service_fee}
                                                    onChange={(e) => setFeeForm((f) => ({ ...f, service_fee: e.target.value }))}
                                                />
                                                <textarea
                                                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm sm:col-span-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                    placeholder="Remark"
                                                    value={feeForm.remark}
                                                    onChange={(e) => setFeeForm((f) => ({ ...f, remark: e.target.value }))}
                                                />
                                                {order.tax_type === "road_tax" && (
                                                    <select
                                                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm sm:col-span-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                        value={feeForm.duration}
                                                        onChange={(e) => setFeeForm((f) => ({ ...f, duration: e.target.value }))}
                                                    >
                                                        <option value="">Select Tax Duration</option>
                                                        {ROAD_TAX_DURATIONS.map((d) => (
                                                            <option key={d.key} value={d.key}>{d.label}</option>
                                                        ))}
                                                    </select>
                                                )}

                                                {/* issue screenshots picker */}
                                                <div className="sm:col-span-2">
                                                    <label className="text-xs font-medium text-gray-500 mb-1 block">
                                                        Issue screenshots (optional, up to 5)
                                                    </label>
                                                    <input
                                                        ref={issueImagesInputRef}
                                                        type="file"
                                                        accept="image/*"
                                                        multiple
                                                        className="hidden"
                                                        onChange={onIssueFilesChange}
                                                    />
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={handleIssueFilesPick}
                                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Attach screenshots
                                                        </button>
                                                        {issueFiles.length > 0 && (
                                                            <span className="text-xs text-gray-500">{issueFiles.length} file(s) selected</span>
                                                        )}
                                                    </div>
                                                    {issueFiles.length > 0 && (
                                                        <div className="mt-2 flex flex-wrap gap-2">
                                                            {issueFiles.map((file, idx) => (
                                                                <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded-md text-gray-600">
                                                                    {file.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* already uploaded issue screenshots — click to zoom */}
                                                {order.any_issue_image?.length > 0 && (
                                                    <div className="sm:col-span-2">
                                                        <label className="text-xs font-medium text-gray-500 mb-1 block">
                                                            Previously uploaded issue screenshots
                                                        </label>
                                                        <div className="flex flex-wrap gap-3">
                                                            {order.any_issue_image.map((img, idx) => (
                                                                <button
                                                                    key={img.public_id || idx}
                                                                    type="button"
                                                                    onClick={() => setPreviewImage(img.url)}
                                                                    className="block h-20 w-20 overflow-hidden rounded-lg border border-gray-200 hover:opacity-80 transition"
                                                                >
                                                                    <img src={img.url} alt={`issue-${idx}`} className="h-full w-full object-cover" />
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="mt-3 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                                                disabled={savingFee}
                                                onClick={submitFinalTax}
                                            >
                                                {savingFee && <Spinner className="h-4 w-4" />}
                                                {savingFee ? "Saving..." : order.final_tax_amount ? "Update Final Tax" : "Submit Final Tax"}
                                            </button>
                                        </div>

                                        <div>
                                            <h4 className="mb-2 text-sm font-semibold text-gray-900">Status History</h4>
                                            <ul className="space-y-2">
                                                {(order.statusHistory || []).map((h, i) => (
                                                    <li key={i} className="flex flex-col gap-0.5 border-b border-gray-100 pb-2 text-xs last:border-0">
                                                        <span className="font-medium text-gray-800">{h.status}</span>
                                                        <span className="text-gray-500">{h.remark}</span>
                                                        <span className="text-gray-400">{new Date(h.changedAt).toLocaleString()}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {/* ---------- IMAGE PREVIEW MODAL ---------- */}
                {previewImage && (
                    <div
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
                        onClick={() => setPreviewImage(null)}
                    >
                        <button
                            className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                            onClick={() => setPreviewImage(null)}
                        >
                            ✕
                        </button>
                        <img
                            src={previewImage}
                            alt="issue screenshot"
                            className="max-h-[90vh] max-w-[92vw] rounded-lg object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}
                {/* ---------- DRIVER INFO MODAL ---------- */}
                {driverModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                        <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="text-base font-semibold text-gray-900">Driver Info</h3>
                                <button className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" onClick={() => setDriverModalOpen(false)}>✕</button>
                            </div>

                            {driverLoading ? (
                                <div className="flex justify-center p-6"><Spinner className="h-6 w-6 text-gray-400" /></div>
                            ) : driverError ? (
                                <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-600">{driverError}</div>
                            ) : driverData ? (
                                <div className="space-y-2 text-sm">
                                    <Field label="Name">{driverData.driver_name || driverData.name || "-"}</Field>
                                    <Field label="Phone">{driverData.driver_contact_number || "-"}</Field>
                                    <Field label="Vehicle">
                                        {driverData?.current_vehicle_id?.vehicle_name - driverData?.current_vehicle_id?.vehicle_type || "-"}
                                    </Field>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">No data.</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProviderTaxRequests;